/**
 * <Seo /> — the one place this app writes its <head>.
 *
 * HOW IT IS USED
 *   `src/App.js` renders a single <Seo /> with no props. It reads the
 *   current path and takes its copy from `config.js`. So every route is
 *   covered, and a new page cannot be forgotten.
 *
 *   A page may render its own <Seo title="..." /> to override one value.
 *   The last one rendered wins for a tag both of them set, which is how
 *   react-helmet-async works. Tasks 14 and 15 use this for a venue's own
 *   title and JSON-LD.
 *
 *   Note what override does NOT do: Helmet merges the two instances, so a
 *   tag only one of them sets still appears. A page that renders
 *   <Seo noindex /> therefore keeps the share card this component already
 *   emitted for that route. If a page ever needs a private head, give its
 *   route a private entry in config.js instead.
 *
 * WHY public/index.html CARRIES `data-rh="true"`
 *   react-helmet-async only removes tags that carry that attribute. Without
 *   it the static tags in `public/index.html` stay on the page and this
 *   component's tags are ADDED next to them — two canonicals, two
 *   descriptions. The marker lets Helmet replace them instead.
 *
 *   The rule that keeps the two files in step: every tag marked `data-rh`
 *   in public/index.html must also be emitted here for a public route.
 *   The global JSON-LD block is deliberately NOT marked, so task 3's
 *   Organization graph survives on every page.
 */

import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { matchPath, useLocation } from 'react-router-dom';

import { isAnyLocation } from '../helpers';
import { FALLBACK, PRIVATE, ROUTES, SITE } from './config';

/** First entry whose path matches, exactly as <Switch> picks a route. */
export const findRoute = (pathname) =>
  ROUTES.find((route) => matchPath(pathname, { path: route.path, exact: route.exact })) || FALLBACK;

/**
 * The one URL this page wants to be known by.
 *
 * Only the parameters in `keepParams` survive. `start`, `end`, `guests`,
 * `page` and any tracking parameter are dropped, because they vary freely
 * and would turn one result set into unlimited duplicate URLs.
 *
 * A kept parameter must also carry real information. `loc=all` is how
 * `makeSearchUrl` writes "no location chosen" (ANY_LOCATION in
 * `src/components/helpers.js`), and it renders the same page as no `loc`
 * at all. Keeping it would give one page two canonical URLs: the
 * `/search/` listed in the sitemap, and the `?loc=all` that every search
 * link from the homepage produces. So an allow-list has to filter by
 * meaning, not only by parameter name.
 */
export const buildCanonical = (route, search) => {
  if (!route.canonicalPath) return null;

  const params = new URLSearchParams(search);

  // TODO(AEO-13): a venue deep link (`?id=`) is a venue, not the search
  // page, so it must not claim `/search/` as its address. Task 14 gives it
  // `/venue/:slug`. Until then emit no canonical at all — saying nothing is
  // safe, saying the wrong thing folds every venue into one page.
  if (params.get('id')) return null;

  const kept = new URLSearchParams();
  (route.keepParams || []).forEach((name) => {
    const value = params.get(name);
    if (!value) return;
    // Use the app's own definition, so this follows ANY_LOCATION if it changes.
    if (name === 'loc' && isAnyLocation(value)) return;
    kept.set(name, value);
  });

  const query = kept.toString();
  return `${SITE.origin}${route.canonicalPath}${query ? `?${query}` : ''}`;
};

/**
 * Turn a JSON-LD object into script text that cannot escape its own tag.
 *
 * `JSON.stringify` does not escape `<`, so a value holding the text
 * `</script>` would close the block early and the rest would run as HTML.
 * Task 15 puts real venue names — typed by hosts — into this data, so the
 * guard is set now, before there is anything to exploit.
 */
export const serialiseJsonLd = (data) =>
  JSON.stringify(data).replace(/</g, '\\u003c').replace(/\u2028|\u2029/g, (c) =>
    c === '\u2028' ? '\\u2028' : '\\u2029'
  );

const Seo = ({ title, description, canonical, image, noindex, jsonLd, children }) => {
  const { pathname, search } = useLocation();
  const route = findRoute(pathname);

  // An unmatched path is treated as private. The catch-all in config.js
  // means that cannot happen today, but a page is never offered to an
  // engine by accident.
  const isPrivate = typeof noindex === 'boolean' ? noindex : route.visibility === PRIVATE;

  const pageTitle = title || route.title;
  const script = jsonLd ? serialiseJsonLd(jsonLd) : null;

  // A private page says who it is and nothing more. No description and no
  // share card, so a pasted link cannot leak what the page is for.
  if (isPrivate) {
    return (
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="robots" content={SITE.robotsPrivate} />
        {script ? <script type="application/ld+json">{script}</script> : null}
        {children}
      </Helmet>
    );
  }

  const pageDescription = description || route.description;
  const pageCanonical = canonical || buildCanonical(route, search);
  const pageImage = image || SITE.image;
  const isDefaultImage = pageImage === SITE.image;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="robots" content={SITE.robotsPublic} />
      {pageDescription ? <meta name="description" content={pageDescription} /> : null}
      {pageCanonical ? <link rel="canonical" href={pageCanonical} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:title" content={pageTitle} />
      {pageDescription ? <meta property="og:description" content={pageDescription} /> : null}
      {pageCanonical ? <meta property="og:url" content={pageCanonical} /> : null}
      <meta property="og:image" content={pageImage} />
      {/* Dimensions and alt text describe the default image only. */}
      {isDefaultImage ? <meta property="og:image:width" content={SITE.imageWidth} /> : null}
      {isDefaultImage ? <meta property="og:image:height" content={SITE.imageHeight} /> : null}
      {isDefaultImage ? <meta property="og:image:alt" content={SITE.imageAlt} /> : null}

      <meta name="twitter:card" content={SITE.twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      {pageDescription ? <meta name="twitter:description" content={pageDescription} /> : null}
      <meta name="twitter:image" content={pageImage} />

      {script ? <script type="application/ld+json">{script}</script> : null}
      {children}
    </Helmet>
  );
};

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  image: PropTypes.string,
  noindex: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  jsonLd: PropTypes.object,
  children: PropTypes.node
};

export default Seo;
